import React, { useCallback, useEffect, useRef, useState } from "react";

import { format, parseISO } from "date-fns";
import { Eye, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { cn } from "../../lib/utils";

import MarkdownWrapper from "../MarkdownWrapper";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// Ler a conversa sem assumir o atendimento. É o que um supervisor precisa
// para decidir se entra: abrir o ticket de verdade o marcaria como lido e
// tiraria a conversa da fila de quem ia atender.
const TicketPeekDialog = ({ ticket }) => {
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const fimRef = useRef(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get(`/messages/${ticket.id}`, {
        // peek: ler sem marcar como lida nem zerar o contador de quem atende.
        params: { pageNumber: 1, peek: true },
      });
      setMensagens(data.messages || []);
    } catch (err) {
      toastError(err);
    }
    setCarregando(false);
  }, [ticket.id]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  useEffect(() => {
    if (mensagens.length) {
      fimRef.current?.scrollIntoView({ block: "end" });
    }
  }, [mensagens]);

  return (
    <>
      <button
        type="button"
        aria-label={`Espiar a conversa de ${ticket.contact?.name}`}
        title="Espiar conversa"
        onClick={(e) => {
          // O item da lista abre o ticket ao clicar; espiar não pode
          // disparar isso junto.
          e.stopPropagation();
          setAberto(true);
        }}
        className="shrink-0 text-[#0872B9] transition-opacity hover:opacity-70 dark:text-sky-300"
      >
        <Eye className="h-4 w-4" />
      </button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent
          className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="truncate text-base">
              {ticket.contact?.name}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Só leitura — o atendimento continua com quem está nele.
            </p>
          </DialogHeader>

          <div className="flex-1 space-y-2 overflow-y-auto p-4">
            {carregando ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : mensagens.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nenhuma mensagem nesta conversa.
              </p>
            ) : (
              mensagens.map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    "flex",
                    m.fromMe ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-2xl px-3 py-2",
                      m.fromMe ? "bg-primary/10" : "bg-muted",
                    )}
                  >
                    {m.mediaUrl && m.mediaType === "image" ? (
                      <img
                        src={m.mediaUrl}
                        alt=""
                        className="mb-1 max-h-40 rounded-lg object-cover"
                      />
                    ) : null}

                    <div className="whitespace-pre-line wrap-break-word text-sm">
                      {m.body ? (
                        <MarkdownWrapper>{m.body}</MarkdownWrapper>
                      ) : (
                        <span className="text-muted-foreground">
                          [{m.mediaType || "mídia"}]
                        </span>
                      )}
                    </div>

                    <time className="mt-0.5 block text-[10px] tabular-nums text-muted-foreground">
                      {format(parseISO(m.createdAt), "dd/MM HH:mm")}
                    </time>
                  </div>
                </div>
              ))
            )}
            <div ref={fimRef} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketPeekDialog;
